import { Route, useLocation, Navigate, createBrowserRouter, createRoutesFromElements } from 'react-router-dom'
import { lazy } from 'react'

import { TopNav } from '../components'
import { useUser } from '../api/auth'
import { useModule } from '../context/ModuleContext'
import { WithAnalyticsTracking } from './WithAnalyticsTracking'
import { WithAutoLogout } from './WithAutoLogout'
import SentryUserSync from './SentryUserSync'

const Error404 = lazy(() => import('./Errors/404'))
const ErrorUnplanned = lazy(() => import('./Errors/unplanned'))

const LoginPage = lazy(() => import('./Auth/Login'))
const OneTimePasscodePage = lazy(() => import('./Auth/OneTimePasscode'))

const ResetPage = lazy(() => import('./Auth/Reset'))
const ForgotPage = lazy(() => import('./Auth/Forgot'))

const Reports = lazy(() => import('./Reports/index.js'))
const Coordinator = lazy(() => import('./Coordinator/index.js'))
const Review = lazy(() => import('./Reviews/index.js'))
const Feedback = lazy(() => import('./Feedback/index.js'))

const WithHeader = ({ children }) => {
  const module = useModule()
  const menuItems = [
    { text: 'Audit', path: '/reviews/' },
    { text: 'Reports', path: '/reports/' },
    { text: 'Admin', path: '/coordinator/' }
  ]
  return (
    <>
      {<TopNav title={`${module.name} ${module.description}`} menuItems={menuItems} />}
      {children}
    </>
  )
}

const ProtectedRoute = ({ children }) => {
  const { user, error, loading } = useUser()
  const location = useLocation()

  if (!user && !(!error && loading))
    return (
      <Navigate
        replace
        to={{
          pathname: '/login',
          state: { from: location }
        }}
      />
    )

  if (user && user.role) {
    const roleMap = {
      audit: ['/reviews'],
      report: ['/reports'],
      manage: ['/reviews', '/reports'],
      admin: ['/reviews', '/reports', '/coordinator'],
      super: ['/reviews', '/reports', '/coordinator']
    }
    if (!roleMap[user.role].some((root) => location.pathname.startsWith(root)))
      return (
        <Navigate
          replace
          to={{
            pathname: roleMap[user.role][0]
          }}
        />
      )
  }

  return <WithAutoLogout>{children}</WithAutoLogout>
}

export const createRouter = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <>
            <SentryUserSync />
            <WithAnalyticsTracking />
          </>
        }
      >
        <Route path='/' element={<Navigate to='/login' />} />,
        <Route path='login' element={<LoginPage />} />,
        <Route path='onetimepasscode' element={<OneTimePasscodePage />} />,
        <Route path='forgot' element={<ForgotPage />} />,
        <Route path='reset/:token' element={<ResetPage />} />,
        <Route path='/feedback/*' element={<Feedback />} />,
        <Route path='set/:token' element={<ResetPage setting={true} />} />,
        <Route path='/error/down' element={<ErrorUnplanned />} />,
        <Route
          path='/reports/*'
          element={
            <ProtectedRoute>
              <WithHeader>
                <Reports />
              </WithHeader>
            </ProtectedRoute>
          }
        />
        ,
        <Route
          path='/coordinator/*'
          element={
            <ProtectedRoute>
              <WithHeader>
                <Coordinator />
              </WithHeader>
            </ProtectedRoute>
          }
        />
        ,
        <Route
          path='/reviews/*'
          element={
            <ProtectedRoute>
              <WithHeader>
                <Review
                  navigationBlocker={{
                    routePrefix: '/reviews/',
                    excludeNextRoutes: ['/login', '/error/down'],
                    excludeSubroutes: ['/confirm']
                  }}
                />
              </WithHeader>
            </ProtectedRoute>
          }
        />
        ,
        <Route path='*' element={<Error404 />} />
      </Route>
    )
  )
